import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoginService } from './login.service';

interface PurchaseQueryParams {
    idClient: string;
    state: number;
    filter: number;
    limit: number;
}

interface PurchaseDetail {
    id: string;
    date: string;
    total: number;
    state: string;
    products: Array<{
        id: number;
        name: string;
        quantity: number;
        price: number;
    }>;
}

interface PurchaseResponse {
    empty: boolean;
    description?: string;
    purchases?: PurchaseDetail[];
}

@Injectable({
    providedIn: 'root'
})
export class HistoryService {
    private apiUrl: string;

    constructor(
        private http: HttpClient,
        private loginService: LoginService
    ) {
        this.apiUrl = environment.API_BACK;
    }

    private getLang(): string {
        return sessionStorage.getItem('lang') || 'es';
    }

    private handleError(error: HttpErrorResponse) {
        console.error('[HistoryService] Error en la petición:', error);

        if (error.status === 401) {
            this.loginService.logout();
            return throwError(() => new Error('Sesión expirada o inválida'));
        }

        return throwError(() => new Error(error.message || 'Error en el servidor'));
    }

    private getUrlWithParams(base: string, params: PurchaseQueryParams): string {
        const queryParams = new URLSearchParams();
        queryParams.append('idclient', params.idClient);
        queryParams.append('state', params.state.toString());
        queryParams.append('filters', params.filter.toString());
        queryParams.append('limit', params.limit.toString());
        queryParams.append('lang', this.getLang());
        return `${this.apiUrl}${base}?${queryParams.toString()}`;
    }

    /**
     * Obtiene el listado de compras silvícolas
     * @param params Parámetros de consulta
     * @returns Observable con la lista de compras
     */
    public getPurchasesSilvicolas(params: PurchaseQueryParams): Observable<PurchaseResponse> {
        console.debug('[HistoryService] Obteniendo compras silvícolas:', params);
        const url = this.getUrlWithParams('purchase/listSil', params);

        // Crear headers específicos
        const headers = this.loginService.getHeaders(true)
            .set('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.get<PurchaseResponse>(url, { headers }).pipe(
            map(response => {
                console.debug('[HistoryService] Respuesta compras silvícolas:', response);
                return response;
            }),
            catchError(error => this.handleError(error))
        );
    }

    /**
     * Obtiene el listado de compras lúdicas
     * @param params Parámetros de consulta
     * @returns Observable con la lista de compras
     */
    public getPurchasesLudicos(params: PurchaseQueryParams): Observable<PurchaseResponse> {
        console.debug('[HistoryService] Obteniendo compras lúdicas:', params);
        const url = this.getUrlWithParams('purchase/listLud', params);

        const headers = this.loginService.getHeaders(true)
            .set('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.get<PurchaseResponse>(url, { headers }).pipe(
            map(response => {
                console.debug('[HistoryService] Respuesta compras lúdicas:', response);
                return response;
            }),
            catchError(error => this.handleError(error))
        );
    }

    /**
     * Obtiene el detalle de una compra específica
     * @param purchaseId ID de la compra
     * @returns Observable con el detalle de la compra
     */
    public getDetailPurchase(purchaseId: string): Observable<PurchaseDetail[]> {
        if (!purchaseId) {
            return throwError(() => new Error('ID de compra requerido'));
        }

        console.debug(`[HistoryService] Obteniendo detalle de compra: ${purchaseId}`);
        const url = `${this.apiUrl}purchase/detail?id_purchase=${purchaseId}&lang=${this.getLang()}`;

        const headers = this.loginService.getHeaders(true)
            .set('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.get<PurchaseDetail[]>(url, { headers }).pipe(
            map(response => {
                console.debug('[HistoryService] Respuesta detalle compra:', response);
                return response;
            }),
            catchError(error => this.handleError(error))
        );
    }
}
